import React, { useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { lcFlow } from '../../help/flows';
import { EXPORT_LC_STAGES } from '../../help/statusStages';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Grid, Link, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ExportLcService } from '../../services/exportLc.service';
import { PERMISSIONS } from '../../constants/permissions';
import { DUE_STATUS_COLORS, ExportLc, LC_STATUS_COLORS } from '../../interfaces/commercial.interface';
import LcAmendmentPanel from './LcAmendmentPanel';
import LcChecklistPanel from './LcChecklistPanel';
import BackToBackLcPanel from './BackToBackLcPanel';
import LcPackingCreditPanel from './LcPackingCreditPanel'; // W3-B8
import LcLinkedDocumentsPanel from './LcLinkedDocumentsPanel';
import LcStatusDialog, { LC_STATUS_TRANSITIONS } from './LcStatusDialog';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import AttachmentsPanel from '../common/AttachmentsPanel'; // W3-K

const ExportLcDetail: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.LC_MANAGE);
    const canSeePc = hasPermission(PERMISSIONS.FINANCING_VIEW); // W3-B8

    const [lc, setLc] = useState<ExportLc | null>(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [statusOpen, setStatusOpen] = useState(false);

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !id) return;
        try {
            setLoading(true);
            setLc(await ExportLcService.getById(institutionId, id));
        } catch (e) {
            toast.showApiError(e, 'Failed to load LC');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    if (!lc) return <Box sx={{ p: 3 }}><Typography>LC not found.</Typography></Box>;

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.lcDetail.page">
            <FlowStrip data-help="commercial.lc.flow" stages={lcFlow({ current: 'lc', lc: { no: lc.lcNumber }, btbHint: 'Back-to-back LCs of this LC are listed in the Back-to-back tab.' })} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h5">{lc.lcNumber}</Typography>
                    <Typography variant="body2" color="text.secondary">{lc.issuingBank} · {lc.buyerName}</Typography>
                </Box>
                {canManage && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {LC_STATUS_TRANSITIONS[lc.status].length > 0 && (
                            <Button data-help="commercial.lcDetail.changeStatus" startIcon={<SwapHorizIcon />} variant="outlined" onClick={() => setStatusOpen(true)}>
                                Change status
                            </Button>
                        )}
                        <Button data-help="commercial.lcDetail.edit" startIcon={<EditIcon />} variant="outlined" onClick={() => navigate(`/commercial/lc/${lc.id}/edit`)}>
                            Edit
                        </Button>
                    </Box>
                )}
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box><StatusStagesChip data-help="commercial.lc.statusStages" label={enumLabel(lc.status)} status={lc.status} stages={EXPORT_LC_STAGES} color={LC_STATUS_COLORS[lc.status]} /></Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Due Status</Typography>
                        <Box>
                            <Tooltip title={lc.dueReason ?? ''}>
                                <Chip
                                    size="small"
                                    data-testid="lc-due-status"
                                    label={lc.dueReason ? `${enumLabel(lc.dueStatus)} — ${lc.dueReason}` : enumLabel(lc.dueStatus)}
                                    color={DUE_STATUS_COLORS[lc.dueStatus]}
                                />
                            </Tooltip>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">LC Amount</Typography>
                        <Typography>{formatMoney(lc.lcAmount, lc.currencyCode)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Effective Max</Typography>
                        <Typography>{formatMoney(lc.effectiveMaxValue, lc.currencyCode)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Issue Date</Typography>
                        <Typography>{formatDate(lc.issueDate)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
                        <Typography>{formatDate(lc.expiryDate)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Latest Shipment</Typography>
                        <Typography>{lc.latestShipmentDate ? formatDate(lc.latestShipmentDate) : '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Buyer</Typography>
                        <Typography>
                            {lc.buyerId ? (
                                <Link component={RouterLink} to="/merchandising/buyers" underline="hover">{lc.buyerName}</Link>
                            ) : lc.buyerName}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Buyer order</Typography>
                        <Typography>
                            {lc.salesOrderId ? (
                                <Link component={RouterLink} to={`/merchandising/orders/${lc.salesOrderId}`} underline="hover">
                                    {lc.salesOrderRef || 'View order'}
                                </Link>
                            ) : (lc.salesOrderRef || '—')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Amendments</Typography>
                        <Typography>{lc.amendmentCount}{lc.amended ? ' (amended)' : ''}</Typography>
                    </Grid>
                    {lc.notes && (
                        <Grid size={12}>
                            <Typography variant="caption" color="text.secondary">Notes</Typography>
                            <Typography>{lc.notes}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Amendments" />
                <Tab label="Document checklist" />
                <Tab label="Back-to-Back" />
                <Tab label="Related documents" />
                {canSeePc && <Tab label="Packing credit" />}
            </Tabs>

            {tab === 0 && <LcAmendmentPanel lcId={lc.id} />}
            {tab === 1 && <LcChecklistPanel lcId={lc.id} />}
            {tab === 2 && <BackToBackLcPanel masterLcId={lc.id} masterCurrencyCode={lc.currencyCode} />}
            {tab === 3 && <LcLinkedDocumentsPanel lc={lc} />}
            {tab === 4 && canSeePc && <LcPackingCreditPanel lcId={lc.id} />}

            {lc.id && <AttachmentsPanel entityType="EXPORT_LC" entityId={lc.id} canManage={canManage} />} {/* W3-K */}

            <LcStatusDialog open={statusOpen} lc={lc} onClose={() => setStatusOpen(false)} onUpdated={setLc} />
        </Box>
    );
};

export default ExportLcDetail;
